import Pusher, { type Channel } from "pusher-js";

import { getApiConfig } from "@/config/api.config";
import { BaseApiService } from "@/services/api/BaseApiService";
import { CentralRfidActivityCallback, PusherCallback } from "@/types/pusher";

export class PusherService extends BaseApiService {
  private static instance: PusherService;
  private pusher: Pusher | null = null;
  private channels: Map<number, Channel> = new Map();
  private centralRfidStoreChannels: Map<number, Channel> = new Map();
  private authToken: string;

  private constructor() {
    super();
    this.authToken = "";
  }

  public static getInstance(): PusherService {
    if (!PusherService.instance) {
      PusherService.instance = new PusherService();
    }
    return PusherService.instance;
  }

  public initialize(authToken: string): void {
    this.authToken = authToken;
    const apiConfig = getApiConfig();

    // If we already have a pusher instance, clean it up first
    if (this.pusher) {
      this.disconnect();
    }

    this.pusher = new Pusher(apiConfig.pusher.key, {
      cluster: apiConfig.pusher.cluster,
      channelAuthorization: {
        endpoint: `${apiConfig.core}/pusher/auth/v2`,
        transport: "ajax",
        headers: {
          Authorization: `Bearer ${this.authToken}`,
        },
      },
    });

    // Add connection state change handlers
    this.pusher.connection.bind("connecting", () => {});

    this.pusher.connection.bind("connected", () => {});

    // we need a better way to handle disconnects and error.
    // reconnect all won't work because we would need to keep track of all the callback which could lead to a memory leaks
    // might be best to pass back an error to the user or the calling component?
    this.pusher.connection.bind("disconnected", () => {});

    this.pusher.connection.bind("error", (_err: any) => {});
  }

  public subscribe(roomId: number, callback: PusherCallback): void {
    if (!this.pusher) {
      throw new Error("Pusher not initialized. Call initialize() first.");
    }

    const channelName = this.buildChannelName(roomId);

    if (this.channels.has(roomId)) {
      return;
    }

    const channel = this.pusher.subscribe(channelName);

    channel.bind("CHANGING_ROOM_UPDATED", (data: string) => {
      callback(roomId, data);
    });

    this.channels.set(roomId, channel);
  }

  public unsubscribe(roomId: number): void {
    if (!this.pusher) {
      return;
    }

    if (this.channels.has(roomId)) {
      this.pusher.unsubscribe(this.buildChannelName(roomId));
      this.channels.delete(roomId);
    }
  }

  public subscribeCentralRfidActivity(
    storeId: number,
    callback: CentralRfidActivityCallback,
  ): void {
    if (!this.pusher) {
      throw new Error("Pusher not initialized. Call initialize() first.");
    }

    if (this.centralRfidStoreChannels.has(storeId)) {
      return;
    }

    const channel = this.pusher.subscribe(this.buildStoreChannelName(storeId));
    channel.bind("CENTRAL_RFID_ACTIVITY", callback);
    this.centralRfidStoreChannels.set(storeId, channel);
  }

  public unsubscribeCentralRfidActivity(storeId: number): void {
    if (!this.pusher || !this.centralRfidStoreChannels.has(storeId)) {
      return;
    }

    this.pusher.unsubscribe(this.buildStoreChannelName(storeId));
    this.centralRfidStoreChannels.delete(storeId);
  }

  public unsubscribeAll(): void {
    if (!this.pusher) {
      return;
    }

    this.channels.forEach((_, roomId) => {
      this.pusher!.unsubscribe(this.buildChannelName(roomId));
    });
    this.channels.clear();
    this.centralRfidStoreChannels.forEach((_, storeId) => {
      this.pusher!.unsubscribe(this.buildStoreChannelName(storeId));
    });
    this.centralRfidStoreChannels.clear();
  }

  public disconnect(): void {
    if (this.pusher) {
      this.unsubscribeAll();
      this.pusher.disconnect();
      this.pusher = null;

      // Clear auth attempts tracking on disconnect
    }
  }

  public getConnectionState(): string {
    return this.pusher ? this.pusher.connection.state : "not initialized";
  }

  public getPusher(): Pusher | null {
    return this.pusher;
  }

  private buildChannelName(roomId: number): string {
    return `private-cache-room-${roomId}`;
  }

  private buildStoreChannelName(storeId: number): string {
    return `private-store-${storeId}`;
  }
}
